<script>
  import { onMount } from 'svelte';
  import { navigate as goto } from 'svelte-routing';
  import ParticipantDetails from '$lib/ParticipantDetails.svelte';
  import FeatureInfo from '$lib/FeatureInfo.svelte';
  import { selectedStudy, selectedParticipant } from '$stores/selectionStore';
  import {
    config,
    delayHours,
    studyProperties,
    isFeatureEnabled,
    fetchStudyProperties,
  } from '$stores/configStore';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import {
    faChevronUp,
    faChevronDown,
    faRotate,
    faCalendarDay,
  } from '@fortawesome/pro-light-svg-icons';
  import StudyHeader from '$lib/StudyHeader.svelte';
  import Toolbar from '$lib/Toolbar.svelte';
  import PrintButton from '$lib/PrintButton.svelte';
  import { page } from '$stores/pageStore';
  import { isCompactMode } from '$stores/uiStore';
  import AGPChart from './agp/AGPChart.svelte';
  import DailyGlucoseProfile from './agp/DailyGlucoseProfile.svelte';
  import TimeInRangeChart from './agp/TimeInRangeChart.svelte';
  import AGPCards from '$lib/AGPCards.svelte';

  // Use local API endpoints instead of direct API with key
  const API_BASE_URL = '/api/proxy';

  // Folded from former +page.server.js: derive initial state from current URL.
  const _initSp = $page.url.searchParams;
  const _initDate = _initSp.get('date');
  const _initDays = parseInt(_initSp.get('days') || '14');
  const _initType = _initSp.get('type') || 'CGM';

  let agps = [];
  let chartAgps = [];
  let compliancesCount = 0;
  let dayRange = _initDays || 14; // days
  let bucketSize = 60; // minutes
  let isLoading = true; // Start as true to match expected initial state
  let chartVisible = true;
  let selectedType = _initType;
  let selectedDate = _initDate || '';
  let dailyReadings = {};
  let allDates = []; // New array to store all dates in range
  let isInitialized = false; // Flag to prevent multiple fetches during initialization
  let isMounted = false; // Track if component is mounted
  let isInitialLoad = true; // Track if this is the first data load

  // Custom date range state
  let isCustomRange = false;
  let customStartDate = '';

  // Feature flag for AGP report card view
  let isAgpReportCardViewEnabled = true;

  // Hydration-safe reactive statements - only run after component is mounted
  // to prevent server/client rendering mismatches
  
  // Reactive variable for feature flags (only after mount to prevent hydration issues)
  $: if (isMounted) {
    isAgpReportCardViewEnabled = isFeatureEnabled('cardViews.agpReport');
  }

  // Subscribe to selectedStudy changes to fetch properties (only after mount)
  $: if (isMounted && $selectedStudy?._id) {
    fetchStudyProperties($selectedStudy._id);
  } else if (isMounted) {
    studyProperties.set(null);
  }

  // Note: Unit changes will be handled by the chart components themselves
  // to prevent infinite loops in the main page

  // Add this helper function to generate date range
  function generateDateRange(endDate, days) {
    const dates = [];
    const end = new Date(endDate + 'T00:00:00'); // Ensure midnight on the end date

    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(end);
      date.setDate(end.getDate() - i);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      dates.push(`${year}-${month}-${day}`);
    }
    return dates;
  }

  /**
   * Fetches AGP (Ambulatory Glucose Profile) data for the selected participant
   * @param {number} days - Number of days to fetch data for
   */
  async function fetchAGP(days = 7) {
    // Guard against running without necessary data
    if (!$selectedParticipant?._id) {
      isLoading = false;
      return;
    }

    try {
      // Only show loading state if not initial load to prevent hydration flash
      if (!isInitialLoad) {
        isLoading = true;
      }
      // Generate all dates in range
      allDates = generateDateRange(selectedDate, days);

      let url = `${API_BASE_URL}/glucoseReadings/agp?subjectGUID=${$selectedParticipant._id}&type=${selectedType}`;

      if (selectedDate) {
        url += `&day=${selectedDate}&days=${days}&timezone=${$selectedParticipant.timezoneCode}`;
      }

      if ($delayHours > 0) {
        url += `&delayHours=${$delayHours}`;
      }

      const res = await fetch(url);

      if (!res.ok) {
        throw new Error('Failed to fetch cgm');
      }

      const data = await res.json();
      const readings = data.data || [];
      compliancesCount = readings.length || 0;
      agps = readings;

      // Group readings into n-minute buckets for full 24 hours
      const timeGroupedReadings = {};
      const TOTAL_MINUTES = 24 * 60; // 1440 minutes in a day

      // Initialize all buckets for 24 hours
      for (let i = 0; i < TOTAL_MINUTES; i += bucketSize) {
        timeGroupedReadings[i] = [];
      }

      readings.forEach((reading) => {
        // Convert reading time to participant's local timezone
        const readDate = new Date(reading.read).toLocaleString('en-US', {
          timeZone: $selectedParticipant.timezoneCode,
        });
        const localDate = new Date(readDate);

        // Calculate minutes based on local time
        const minutes = localDate.getHours() * 60 + localDate.getMinutes();
        const bucketKey = Math.floor(minutes / bucketSize) * bucketSize;

        if (bucketKey >= 0 && bucketKey < TOTAL_MINUTES) {
          timeGroupedReadings[bucketKey].push(reading.value);
        }
      });

      // Calculate percentiles for each time bucket
      let agpData = Object.entries(timeGroupedReadings).map(([minute, values]) => {
        values.sort((a, b) => a - b);
        return {
          minute: parseInt(minute),
          p5: percentile(values, 5),
          p25: percentile(values, 25),
          p50: percentile(values, 50),
          p75: percentile(values, 75),
          p95: percentile(values, 95),
        };
      });

      // Sort by minute of day
      agpData.sort((a, b) => a.minute - b.minute);

      // Append first hour's data to the end (for smooth transition in chart)
      const firstHourData = agpData
        .filter((d) => d.minute < 60)
        .map((d) => ({
          ...d,
          minute: d.minute + TOTAL_MINUTES,
        }));

      agpData = [...agpData, ...firstHourData];

      chartAgps = agpData;
      isLoading = false;
      isInitialLoad = false; // Mark initial load as complete

      // Initialize dailyReadings with empty arrays for all dates
      dailyReadings = Object.fromEntries(allDates.map((date) => [date, []]));

      // Then populate with actual readings
      readings.forEach((reading) => {
        // Convert reading time to participant's local timezone
        const readDate = new Date(reading.read).toLocaleString('en-US', {
          timeZone: $selectedParticipant.timezoneCode,
        });
        const localDate = new Date(readDate);

        // Create a date key that represents midnight-to-midnight in participant's timezone
        const year = localDate.getFullYear();
        const month = String(localDate.getMonth() + 1).padStart(2, '0');
        const day = String(localDate.getDate()).padStart(2, '0');
        const dateKey = `${year}-${month}-${day}`;

        // Only add to dailyReadings if this date is in our allDates array
        if (allDates.includes(dateKey)) {
          if (!dailyReadings[dateKey]) {
            dailyReadings[dateKey] = [];
          }

          // Create a new reading object with the local time
          const localReading = {
            ...reading,
            read: localDate.toISOString(), // Convert local date back to ISO string
          };

          dailyReadings[dateKey].push(localReading);
        }
      });

      // Sort readings within each day
      Object.keys(dailyReadings).forEach((date) => {
        dailyReadings[date].sort((a, b) => new Date(a.read) - new Date(b.read));
      });
    } catch (err) {
      isLoading = false;
      console.error('Error fetching agps:', err);
      chartAgps = [];
      agps = [];
    }
  }

  async function fetchLastCgmDay() {
    try {
      let url = `${API_BASE_URL}/glucoseReadings/daily?page=1&pageSize=1&sortOrder=desc&subjectGUID=${$selectedParticipant._id}&timezone=${$selectedParticipant.timezoneCode}&type=CGM&inRange=false`;
      if ($delayHours > 0) {
        url += `&delayHours=${$delayHours}`;
      }
      const res = await fetch(url);
      if (!res.ok) {
        throw new Error('Failed to fetch last CGM day');
      }
      const data = await res.json();
      const first = data?.data?.[0];
      return first?.day || '';
    } catch (err) {
      console.error('Error fetching last CGM day:', err);
      return '';
    }
  }

  // Helper function to calculate percentiles
  function percentile(arr, p) {
    if (arr.length === 0) return null;
    if (arr.length === 1) return arr[0];

    const index = (p / 100) * (arr.length - 1);
    const lower = Math.floor(index);
    const upper = Math.ceil(index);
    const weight = index % 1;

    if (upper === lower) return arr[index];

    return arr[lower] * (1 - weight) + arr[upper] * weight;
  }

  onMount(async () => {
    // Set mounted first to prevent rendering flash
    isMounted = true;
    
    // Small delay to ensure hydration is complete before fetching data
    await new Promise(resolve => setTimeout(resolve, 10));
    
    // Get date from URL parameter or use default date
    const urlDate = $page.url.searchParams.get('date');
    if (urlDate) {
      selectedDate = urlDate;
    } else {
      // Prefer the randomization date or most recent day with CGM data
      const lastCgmDay = await fetchLastCgmDay();
      if (
        isFeatureEnabled('randomization.enabled') &&
        $selectedParticipant?.randomizationDate
      ) {
        const randomizationDate = new Date($selectedParticipant.randomizationDate);
        const year = randomizationDate.getFullYear();
        const month = String(randomizationDate.getMonth() + 1).padStart(2, '0');
        const day = String(randomizationDate.getDate()).padStart(2, '0');
        selectedDate = `${year}-${month}-${day}`;
      } else if (lastCgmDay) {
        selectedDate = lastCgmDay;
      } else  {
        const localDate = new Date().toLocaleDateString('en-US', {
          timeZone: $selectedParticipant.timezoneCode,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        });
        const [month, day, year] = localDate.split('/');
        selectedDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }

      // Update URL with the selected date
      const url = new URL(window.location);
      url.searchParams.set('date', selectedDate);
      goto(url.toString(), { replaceState: true });
    }

    // Wait for unit to be available before fetching data

    // Wait for both mounted state and participant data before fetching
    if (!$selectedParticipant?._id) {
      isLoading = false; // Don't show loading if we're waiting for participant
      return;
    }

    // Initial fetch
    await fetchAGP(dayRange);
    isInitialized = true;
  });

  async function handleExportCSV() {
    // TODO: Implement CSV export functionality
  }

  async function handleExportExcel() {
    // TODO: Implement Excel export functionality
  }

  function handleRefresh() {
    if (isInitialized) {
      if (isCustomRange) {
        handleCustomRangeQuery();
      } else {
        fetchAGP(dayRange);
      }
    }
  }

  function handleChartVisibility() {
    chartVisible = !chartVisible;
  }

  function handleDateChange() {
    if (!isInitialized) return; // Prevent fetching during initialization

    const url = new URL(window.location);
    if (selectedDate) {
      url.searchParams.set('date', selectedDate);
    } else {
      url.searchParams.delete('date');
    }
    goto(url.toString(), { replaceState: true });
    
    // Skip auto-fetch in custom range mode - user must click Go button
    if (!isCustomRange) {
      fetchAGP(dayRange);
    }
  }

  function handleCustomRangeQuery() {
    if (!customStartDate || !selectedDate) return;
    
    const start = new Date(customStartDate + 'T00:00:00');
    const end = new Date(selectedDate + 'T00:00:00');
    const diffDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
    
    if (diffDays > 0) {
      // Update URL with both dates
      const url = new URL(window.location);
      url.searchParams.set('date', selectedDate);
      goto(url.toString(), { replaceState: true });
      
      fetchAGP(diffDays);
    }
  }
</script>

<StudyHeader />

<FeatureInfo
  title="Ambulatory Glucose Profile"
  description="This page displays an Ambulatory Glucose Profile (AGP) report for the selected participant. You can view glucose patterns across the day,
  time-in-range distribution, and daily glucose profiles. Use the controls to adjust date range, resolution, and delay.

  *Metrics*
  *Average Glucose*: Mean glucose across the selected period
  *GMI* (Glucose Management Indicator): Estimated HbA1c from mean glucose. GMI = 3.31 + 0.02392 x mean glucose (mg/dL)
  *CV* (Coefficient of Variation): Glucose variability (Target: CV <36%)
  *TBR* (Time Below Range): % of readings <{$studyProperties?.glucoseParameters?.thresholds?.low || 70} mg/dL
  *TIR* (Time in Range): % of readings in {$studyProperties?.glucoseParameters?.thresholds?.low || 70}-{$studyProperties?.glucoseParameters?.thresholds?.high || 180} mg/dL
  *TAR* (Time Above Range): % of readings >{$studyProperties?.glucoseParameters?.thresholds?.high || 180} mg/dL
  *AGP Percentiles*:
    Median (50th percentile), IQR (25th-75th), and 90% range (5th-95th) across 24 hours.
  *Events*: Hypo/Hyper episodes use study settings: <{$studyProperties?.glucoseParameters?.events?.hypoThreshold || 54} mg/dL and
  >{$studyProperties?.glucoseParameters?.events?.hyperThreshold || 250} mg/dL for >{($studyProperties?.glucoseParameters?.events?.eventDuration || 3) * 5} mins."
  marginBottom="mb-0"
  descriptionClass={`agp-feature-description ${$isCompactMode ? 'hidden' : ''}`}
/>

{#if $selectedParticipant}
  <ParticipantDetails showOptionsMenu={false} />
{/if}

{#if isAgpReportCardViewEnabled}
  <AGPCards readings={agps} studyProperties={$studyProperties} />
{/if}

<Toolbar title="AGP & Daily Profiles" itemCountTitle="Items" itemCount={compliancesCount}>
  <div class="text-m flex items-center gap-2 print:text-xs print:gap-1">
    {#if isCustomRange}
      <div class="flex items-center gap-1 print:gap-0.5">
        Start
        <input
          type="date"
          bind:value={customStartDate}
          class="h-6 w-32 ms-1 px-1 border rounded dark:bg-gray-700 dark:border-gray-600 text-sm text-gray-800 dark:text-gray-100 print:h-4 print:w-24 print:text-xs"
        />
      </div>
    {/if}
    <div class="flex items-center gap-1 print:gap-0.5">
      End
      <input
        type="date"
        bind:value={selectedDate}
        on:input={handleDateChange}
        class="h-6 w-32 ms-1 px-1 border rounded dark:bg-gray-700 dark:border-gray-600 text-sm text-gray-800 dark:text-gray-100 print:h-4 print:w-24 print:text-xs"
      />
      {#if !isCustomRange}
        <button
          on:click={() => {
            const today = new Date();
            const year = today.getFullYear();
            const month = String(today.getMonth() + 1).padStart(2, '0');
            const day = String(today.getDate()).padStart(2, '0');
            selectedDate = `${year}-${month}-${day}`;
            dayRange = 14;
            bucketSize = 60;
            handleDateChange();
          }}
          title="Set to today"
          class="h-6 pagination mx-1 p-0 hide-on-print"
        >
          <FontAwesomeIcon icon={faCalendarDay} />
        </button>
      {/if}
    </div>
    {#if isCustomRange}
      <button
        on:click={handleCustomRangeQuery}
        class="h-6 px-3 bg-sky-600 hover:bg-sky-700 text-white text-sm rounded hide-on-print"
        title="Apply custom date range"
      >
        Go
      </button>
    {/if}
    <div class="flex items-center gap-1 print:gap-0.5">
      Range
      <select
        bind:value={dayRange}
        on:change={() => {
          if (dayRange === -1) {
            isCustomRange = true;
            // Initialize customStartDate based on current selectedDate if empty
            if (!customStartDate && selectedDate) {
              const end = new Date(selectedDate + 'T00:00:00');
              end.setDate(end.getDate() - 13); // Default to 14 days back
              const year = end.getFullYear();
              const month = String(end.getMonth() + 1).padStart(2, '0');
              const day = String(end.getDate()).padStart(2, '0');
              customStartDate = `${year}-${month}-${day}`;
            }
          } else {
            isCustomRange = false;
            fetchAGP(dayRange);
          }
        }}
        class="form-select h-6 w-24 border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none print:h-4 print:w-20 print:text-xs"
      >
        <option value={-1} selected={dayRange === -1}>Custom</option>
        <option value={1} selected={dayRange === 1}>1 Day</option>
        <option value={7} selected={dayRange === 7}>1 Week</option>
        <option value={14} selected={dayRange === 14}>2 Weeks</option>
        <option value={28} selected={dayRange === 28}>4 Weeks</option>
        <option value={42} selected={dayRange === 42}>6 Weeks</option>
        <option value={56} selected={dayRange === 56}>8 Weeks</option>
        <option value={70} selected={dayRange === 70}>10 Weeks</option>
        <option value={84} selected={dayRange === 84}>12 Weeks</option>
      </select>
    </div>
    <div class="flex items-center gap-1 print:hidden">
      Resolution
      <select
        bind:value={bucketSize}
        on:change={() => {
          if (isCustomRange) {
            handleCustomRangeQuery();
          } else {
            fetchAGP(dayRange);
          }
        }}
        class="form-select h-6 w-28 border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none print:h-4 print:w-24 print:text-xs"
      >
        <option value={1} selected={bucketSize === 1}>1 Minutes</option>
        <option value={5} selected={bucketSize === 5}>5 Minutes</option>
        <option value={15} selected={bucketSize === 15}>15 Minutes</option>
        <option value={30} selected={bucketSize === 30}>30 Minutes</option>
        <option value={60} selected={bucketSize === 60}>1 Hour</option>
      </select>
    </div>
    {#if $config.delayVisible}
      <div class="flex items-center mx-2 hide-on-print">
        <span class="me-1">Delay</span>
        <select
          bind:value={$delayHours}
          on:change={() => handleRefresh()}
          class="form-select h-6 w-24 border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
        >
          <option value={0}>None</option>
          <option value={3}>3 Hours</option>
        </select>
      </div>
    {:else if $delayHours > 0}
      <div class="flex items-center mx-2 hide-on-print">
        <span class="me-1">Delay {$delayHours} hours</span>
      </div>
    {/if}
    <div class="flex items-center gap-1 hide-on-print">
      <button
        on:click={handleChartVisibility}
        class="pagination mx-1 p-0 print:mx-0.5"
        title="Toggle chart visibility"
      >
        {#if chartVisible}
          <FontAwesomeIcon icon={faChevronDown} />
        {:else}
          <FontAwesomeIcon icon={faChevronUp} />
        {/if}
      </button>
      <button
        on:click={() => handleRefresh()}
        class="pagination mx-1 p-0 hide-on-print"
        title="Refresh"
      >
        <FontAwesomeIcon icon={faRotate} />
      </button>
      <PrintButton />
    </div>
  </div>
</Toolbar>

<!-- Hydration-safe loading states: prevents flash by showing consistent loading until all data is ready -->
{#if !isMounted || (isLoading && isInitialLoad) || !$selectedParticipant?._id}
  <div class="flex flex-col justify-center items-center h-64 bg-stone-100/50 dark:bg-gray-725 mx-1">
    <div class="loader mb-4"></div>
    <p class="text-xs justify-center text-gray-700 dark:text-gray-300">
      {#if !$selectedParticipant?._id}
        loading participant data...
      {:else}
        preparing AGP report
      {/if}
    </p>
  </div>
{:else if isLoading}
  <!-- Secondary loading state for subsequent fetches -->
  <div class="flex flex-col justify-center items-center h-16 bg-stone-100/50 dark:bg-gray-725 mx-1">
    <div class="loader-small mb-2"></div>
    <p class="text-xs justify-center text-gray-700 dark:text-gray-300">updating data...</p>
  </div>
{:else}
  {#if chartVisible}
    <div
      class="flex flex-row pb-5 mt-4 mx-3 print:w-auto print:max-w-full print:h-auto print:scale-[0.85] print:origin-top-left"
    >
      {#if chartAgps.length > 0}
        <div class="w-2/3" style="height: 400px;">
          <AGPChart agpData={chartAgps} />
        </div>
        <div class="w-1/3" style="height: 400px;">
          <TimeInRangeChart readings={agps} />
        </div>
      {/if}
    </div>
  {/if}

  {#if Object.keys(dailyReadings).length > 0}
    <div class="mt-4 print:break-before-page">
      <h3 class="text-l mb-4 mx-4 text-sky-800 dark:text-white">Daily Glucose Profiles</h3>
      <div class="flex flex-wrap justify-start px-4">
        {#each allDates as date}
          <div
            role="button"
            tabindex="0"
            on:click={() => {
              selectedDate = date;
              dayRange = 1;
              bucketSize = 15;
              isCustomRange = false;
              handleDateChange();
            }}
            on:keydown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                selectedDate = date;
                dayRange = 1;
                bucketSize = 15;
                isCustomRange = false;
                handleDateChange();
              }
            }}
            class="cursor-pointer"
          >
            <DailyGlucoseProfile readings={dailyReadings[date] || []} {date} />
          </div>
        {/each}
      </div>
    </div>
  {/if}
{/if}

<style>
  .loader {
    border: 2px solid #20669c;
    border-radius: 50%;
    border-top: 4px solid #98acb4;
    width: 32px;
    height: 32px;
    animation: spin 2s linear infinite;
  }

  .loader-small {
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-radius: 50%;
    border-top: 2px solid #3498db;
    width: 20px;
    height: 20px;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
  /* Print preparation styling */
  :global(.print-preparation) {
    cursor: wait;
  }

  :global(.print-preparation button[title='Preparing print...']) {
    opacity: 0.5;
    cursor: wait;
  }

  /* Ensure all chart canvases have white background in print mode */
  @media print {
    canvas {
      background-color: white !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    :global(.agp-feature-description) {
      display: none !important;
    }
  }
</style>
